import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
    Users,
    Mic,
    Briefcase,
    Search,
    Video,
    TrendingUp,
    Trophy,
    Flame,
    Download,
    ArrowRight,
    BarChart3,
    Building2,
    Clock,
    Target,
    Activity,
    Loader2,
    RefreshCw,
    GraduationCap,
    DollarSign,
    ChevronLeft,
    ChevronRight,
    X
} from 'lucide-react';
import { AnalyticsChart } from '../charts/AnalyticsChart';

const AdminComprehensiveDashboard = () => {
    const { api } = useAuth();
    const [stats, setStats] = useState(null);
    const [leaderboard, setLeaderboard] = useState(null);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [timeRange, setTimeRange] = useState('all');
    const [department, setDepartment] = useState('all');
    const [departments, setDepartments] = useState([]);
    
    // Modal state for detailed views
    const [detailModal, setDetailModal] = useState({ open: false, type: null, title: '', data: [], loading: false });
    const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0 });

    const fetchDashboardData = useCallback(async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            if (timeRange !== 'all') params.append('days', timeRange);
            if (department !== 'all') params.append('department', department);
            
            const [statsRes, leaderboardRes, deptRes] = await Promise.all([
                api.get(`/dashboard/admin/comprehensive?${params}`),
                api.get('/dashboard/admin/leaderboard?feature=all&limit=5'),
                api.get('/users/departments/list')
            ]);
            
            setStats(statsRes.data);
            setLeaderboard(leaderboardRes.data.leaderboards);
            setDepartments(deptRes.data.departments || []);
        } catch (error) {
            console.error('Failed to fetch dashboard:', error);
            toast.error('Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    }, [api, timeRange, department]);

    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);

    // Fetch detailed data for stat card modals
    const openDetailModal = async (type) => {
        const titles = {
            'total_users': 'All Users',
            'active_users': 'Active Users (Last 30 days)',
            'total_sessions': 'All Training Sessions',
            'avg_completion': 'Completion Statistics'
        };
        
        setDetailModal({ open: true, type, title: titles[type], data: [], loading: true });
        setPagination({ page: 1, limit: 10, total: 0 });
        
        try {
            let data = [];
            let total = 0;
            
            if (type === 'total_users' || type === 'active_users') {
                // Fetch users list
                const response = await api.get('/users');
                let users = response.data || [];
                
                // Apply department filter if selected
                if (department && department !== 'all') {
                    users = users.filter(u => u.department === department);
                }
                
                // Filter for active users based on timeRange if needed
                if (type === 'active_users') {
                    let daysThreshold = 30; // Default
                    if (timeRange === '7') daysThreshold = 7;
                    else if (timeRange === '14') daysThreshold = 14;
                    else if (timeRange === '30') daysThreshold = 30;
                    else if (timeRange === '90') daysThreshold = 90;
                    
                    const thresholdDate = new Date();
                    thresholdDate.setDate(thresholdDate.getDate() - daysThreshold);
                    users = users.filter(u => u.last_login && new Date(u.last_login) > thresholdDate);
                }
                
                total = users.length;
                data = users.slice(0, 10); // First page
            } else if (type === 'total_sessions') {
                // Fetch from reports with filters
                const params = new URLSearchParams();
                params.append('tab', 'simulations');
                params.append('limit', '10');
                if (timeRange !== 'all') params.append('days', timeRange);
                if (department !== 'all') params.append('department', department);
                
                const response = await api.get(`/reports?${params}`);
                data = response.data?.data || [];
                total = response.data?.total || data.length;
            } else if (type === 'avg_completion') {
                // Just show feature stats - no API call needed
                data = Object.entries(features).map(([key, val]) => ({
                    name: key.replace(/_/g, ' '),
                    ...val
                }));
                total = data.length;
            }
            
            setDetailModal(prev => ({
                ...prev,
                data,
                loading: false
            }));
            setPagination(prev => ({
                ...prev,
                total
            }));
        } catch (error) {
            console.error('Failed to fetch detail data:', error);
            setDetailModal(prev => ({ ...prev, loading: false }));
            toast.error('Failed to load detailed data');
        }
    };

    const fetchDetailPage = async (page) => {
        setDetailModal(prev => ({ ...prev, loading: true }));
        
        try {
            let data = [];
            const startIdx = (page - 1) * pagination.limit;
            
            if (detailModal.type === 'total_users' || detailModal.type === 'active_users') {
                const response = await api.get('/users');
                let users = response.data || [];
                
                if (detailModal.type === 'active_users') {
                    const thirtyDaysAgo = new Date();
                    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
                    users = users.filter(u => u.last_login && new Date(u.last_login) > thirtyDaysAgo);
                }
                
                data = users.slice(startIdx, startIdx + pagination.limit);
            } else if (detailModal.type === 'total_sessions') {
                const response = await api.get(`/reports?tab=simulations&limit=10&skip=${startIdx}`);
                data = response.data?.data || [];
            }
            
            setDetailModal(prev => ({
                ...prev,
                data,
                loading: false
            }));
            setPagination(prev => ({
                ...prev,
                page
            }));
        } catch (error) {
            setDetailModal(prev => ({ ...prev, loading: false }));
        }
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            const params = new URLSearchParams();
            if (timeRange !== 'all') params.append('days', timeRange);
            if (department !== 'all') params.append('department', department);
            
            const response = await api.get(`/dashboard/admin/export?${params}`, {
                responseType: 'blob'
            });
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `dashboard_export_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            
            toast.success('Dashboard exported successfully');
        } catch (error) {
            toast.error('Failed to export dashboard');
        } finally {
            setExporting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    const features = stats?.features || {};
    const featureCards = [
        {
            key: 'simulations',
            name: 'Recruiter Sims',
            icon: Mic,
            color: 'text-blue-500',
            bgColor: 'bg-blue-500/10',
            link: '/simulation',
            data: features.simulations
        },
        {
            key: 'job_simulations',
            name: 'Job Sims',
            icon: Briefcase,
            color: 'text-purple-500',
            bgColor: 'bg-purple-500/10',
            link: '/job-simulations',
            data: features.job_simulations
        },
        {
            key: 'sourcing_labs',
            name: 'Sourcing Labs',
            icon: Search,
            color: 'text-emerald-500',
            bgColor: 'bg-emerald-500/10',
            link: '/sourcing-labs',
            data: features.sourcing_labs
        },
        {
            key: 'rpat',
            name: 'R-PAT',
            icon: GraduationCap,
            color: 'text-violet-500',
            bgColor: 'bg-violet-500/10',
            link: '/rpat',
            data: features.rpat
        },
        {
            key: 'evolve',
            name: 'Evolve AI',
            icon: Video,
            color: 'text-orange-500',
            bgColor: 'bg-orange-500/10',
            link: '/evolve',
            data: features.evolve
        },
        {
            key: 'inside_sales_training',
            name: 'Inside Sales',
            icon: DollarSign,
            color: 'text-indigo-500',
            bgColor: 'bg-indigo-500/10',
            link: '/inside-sales/analytics',
            data: features.inside_sales_training
        }
    ];

    return (
        <div className="space-y-6" data-testid="admin-comprehensive-dashboard">
            {/* Filters Row */}
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <Select value={timeRange} onValueChange={setTimeRange}>
                        <SelectTrigger className="w-40" data-testid="time-range-filter">
                            <Clock className="h-4 w-4 mr-2" />
                            <SelectValue placeholder="Time Range" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Time</SelectItem>
                            <SelectItem value="7">Last 7 Days</SelectItem>
                            <SelectItem value="30">Last 30 Days</SelectItem>
                            <SelectItem value="90">Last 90 Days</SelectItem>
                        </SelectContent>
                    </Select>
                    
                    <Select value={department} onValueChange={setDepartment}>
                        <SelectTrigger className="w-44" data-testid="department-filter">
                            <Building2 className="h-4 w-4 mr-2" />
                            <SelectValue placeholder="Department" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Departments</SelectItem>
                            {departments.map(dept => (
                                <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    
                    <Button variant="outline" size="icon" onClick={fetchDashboardData} data-testid="refresh-btn">
                        <RefreshCw className="h-4 w-4" />
                    </Button>
                </div>
                
                <Button 
                    variant="outline" 
                    onClick={handleExport} 
                    disabled={exporting}
                    data-testid="export-btn"
                >
                    {exporting ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                        <Download className="h-4 w-4 mr-2" />
                    )}
                    Export CSV
                </Button>
            </div>

            {/* Summary Stats - Clickable Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card 
                    className="cursor-pointer hover:border-primary/50 transition-colors"
                    onClick={() => openDetailModal('total_users')}
                    data-testid="stat-total-users"
                >
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-primary/10">
                                <Users className="h-5 w-5 text-primary" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Total Users</p>
                                <p className="text-2xl font-bold">{stats?.summary?.total_users || 0}</p>
                            </div>
                            <ArrowRight className="h-4 w-4 ml-auto text-muted-foreground" />
                        </div>
                    </CardContent>
                </Card>
                
                <Card 
                    className="cursor-pointer hover:border-emerald-500/50 transition-colors"
                    onClick={() => openDetailModal('active_users')}
                    data-testid="stat-active-users"
                >
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-emerald-500/10">
                                <Activity className="h-5 w-5 text-emerald-500" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Active Users</p>
                                <p className="text-2xl font-bold">{stats?.summary?.active_users || 0}</p>
                            </div>
                            <ArrowRight className="h-4 w-4 ml-auto text-muted-foreground" />
                        </div>
                    </CardContent>
                </Card>
                
                <Card 
                    className="cursor-pointer hover:border-blue-500/50 transition-colors"
                    onClick={() => openDetailModal('total_sessions')}
                    data-testid="stat-total-sessions"
                >
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-blue-500/10">
                                <Target className="h-5 w-5 text-blue-500" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Total Sessions</p>
                                <p className="text-2xl font-bold">
                                    {Object.values(features).reduce((sum, f) => sum + (f?.total_sessions || 0), 0)}
                                </p>
                            </div>
                            <ArrowRight className="h-4 w-4 ml-auto text-muted-foreground" />
                        </div>
                    </CardContent>
                </Card>
                
                <Card 
                    className="cursor-pointer hover:border-purple-500/50 transition-colors"
                    onClick={() => openDetailModal('avg_completion')}
                    data-testid="stat-avg-completion"
                >
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-purple-500/10">
                                <TrendingUp className="h-5 w-5 text-purple-500" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Avg Completion</p>
                                <p className="text-2xl font-bold">
                                    {Math.round(
                                        Object.values(features)
                                            .filter(f => f?.completion_rate > 0)
                                            .reduce((sum, f, _, arr) => sum + (f?.completion_rate || 0) / arr.length, 0)
                                    ) || 0}%
                                </p>
                            </div>
                            <ArrowRight className="h-4 w-4 ml-auto text-muted-foreground" />
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Feature Cards - 5 in a row, compact design */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {featureCards.map(feature => {
                    const Icon = feature.icon;
                    const data = feature.data || {};
                    
                    return (
                        <Link to={feature.link} key={feature.key} data-testid={`feature-card-${feature.key}`}>
                            <Card className="card-hover cursor-pointer h-full group">
                                <CardContent className="p-3 space-y-2">
                                    <div className="flex items-center justify-between">
                                        <div className={`p-1.5 rounded-lg ${feature.bgColor}`}>
                                            <Icon className={`h-4 w-4 ${feature.color}`} />
                                        </div>
                                        <ArrowRight className="h-3 w-3 text-muted-foreground group-hover:text-primary transition-colors" />
                                    </div>
                                    <h3 className="text-sm font-semibold">{feature.name}</h3>
                                    <div className="space-y-1.5">
                                        <div className="flex justify-between text-xs">
                                            <span className="text-muted-foreground">Sessions</span>
                                            <span className="font-medium">{data.total_sessions || 0}</span>
                                        </div>
                                        <div className="flex justify-between text-xs">
                                            <span className="text-muted-foreground">Done</span>
                                            <span className="font-medium">{data.completed || 0}</span>
                                        </div>
                                        <Progress value={data.completion_rate || 0} className="h-1" />
                                        <div className="flex justify-between text-xs pt-1 border-t">
                                            <span className="text-muted-foreground">Avg</span>
                                            <Badge variant="secondary" className="text-xs px-1 py-0">{data.avg_score || 0}</Badge>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </Link>
                    );
                })}
            </div>

            {/* Charts and Leaderboards Row */}
            <div className="grid lg:grid-cols-2 gap-6">
                {/* Feature Performance Chart */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <BarChart3 className="h-5 w-5 text-primary" />
                            Feature Performance
                        </CardTitle>
                        <CardDescription>Average scores across all features</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <AnalyticsChart
                            type="bar"
                            height={200}
                            data={featureCards.map(f => ({
                                label: f.name.split(' ')[0],
                                value: f.data?.avg_score || 0
                            }))}
                        />
                    </CardContent>
                </Card>

                {/* Completion Rates */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <TrendingUp className="h-5 w-5 text-emerald-500" />
                            Completion Rates
                        </CardTitle>
                        <CardDescription>Session completion by feature</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <AnalyticsChart
                            type="donut"
                            height={200}
                            data={featureCards.map((f, i) => ({
                                label: f.name.split(' ')[0],
                                value: f.data?.completed || 0,
                                color: ['hsl(217, 91%, 60%)', 'hsl(271, 91%, 65%)', 'hsl(142, 76%, 36%)', 'hsl(24, 95%, 53%)'][i]
                            }))}
                        />
                    </CardContent>
                </Card>
            </div>

            {/* Leaderboards and Streaks */}
            <div className="grid lg:grid-cols-3 gap-6">
                {/* Top Performers - Simulations */}
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                            <Trophy className="h-4 w-4 text-yellow-500" />
                            Top Performers - Simulations
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {leaderboard?.simulations?.length > 0 ? (
                            <div className="space-y-3">
                                {leaderboard.simulations.slice(0, 5).map((user, i) => (
                                    <div key={user.user_id} className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                                i === 0 ? 'bg-yellow-500 text-white' :
                                                i === 1 ? 'bg-gray-400 text-white' :
                                                i === 2 ? 'bg-amber-700 text-white' :
                                                'bg-muted text-muted-foreground'
                                            }`}>
                                                {i + 1}
                                            </span>
                                            <span className="text-sm truncate max-w-[120px]">{user.user_name || 'Unknown'}</span>
                                        </div>
                                        <Badge variant="secondary">{user.avg_score}</Badge>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-sm text-muted-foreground text-center py-4">No data yet</p>
                        )}
                    </CardContent>
                </Card>

                {/* Top Performers - Sourcing */}
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                            <Trophy className="h-4 w-4 text-emerald-500" />
                            Top Performers - Sourcing
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {leaderboard?.sourcing_labs?.length > 0 ? (
                            <div className="space-y-3">
                                {leaderboard.sourcing_labs.slice(0, 5).map((user, i) => (
                                    <div key={user.user_id} className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                                i === 0 ? 'bg-yellow-500 text-white' :
                                                i === 1 ? 'bg-gray-400 text-white' :
                                                i === 2 ? 'bg-amber-700 text-white' :
                                                'bg-muted text-muted-foreground'
                                            }`}>
                                                {i + 1}
                                            </span>
                                            <span className="text-sm truncate max-w-[120px]">{user.user_name || 'Unknown'}</span>
                                        </div>
                                        <Badge variant="secondary">{user.avg_score}</Badge>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-sm text-muted-foreground text-center py-4">No data yet</p>
                        )}
                    </CardContent>
                </Card>

                {/* Streaks */}
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                            <Flame className="h-4 w-4 text-orange-500" />
                            Top Streaks
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {stats?.streaks?.top_streaks?.length > 0 ? (
                            <div className="space-y-3">
                                {stats.streaks.top_streaks.map((user, i) => (
                                    <div key={user.user_id} className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <Flame className={`h-4 w-4 ${i === 0 ? 'text-orange-500' : 'text-muted-foreground'}`} />
                                            <span className="text-sm truncate max-w-[120px]">{user.user_name || 'Unknown'}</span>
                                        </div>
                                        <Badge variant="outline" className="gap-1">
                                            {user.current_streak} days
                                        </Badge>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-sm text-muted-foreground text-center py-4">No active streaks</p>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Department Breakdown */}
            {stats?.department_breakdown?.length > 0 && (
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Building2 className="h-5 w-5 text-primary" />
                            Department Performance
                        </CardTitle>
                        <CardDescription>Average scores by department</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b">
                                        <th className="text-left py-2 font-medium">Department</th>
                                        <th className="text-center py-2 font-medium">Users</th>
                                        <th className="text-center py-2 font-medium">Avg Voice</th>
                                        <th className="text-center py-2 font-medium">Avg Communication</th>
                                        <th className="text-center py-2 font-medium">Avg Simulation</th>
                                    </tr>
                                </thead>
                                <TableBody>
                                    {stats.department_breakdown.map(dept => {
                                        return (
                                            <tr key={dept.department} className="border-b last:border-0" data-testid={`dept-row-${dept.department}`}>
                                                <td className="py-2 font-medium">{dept.department}</td>
                                                <td className="py-2 text-center">{dept.user_count}</td>
                                                <td className="py-2 text-center">
                                                    <Badge variant="outline">{dept.avg_scores.voice}</Badge>
                                                </td>
                                                <td className="py-2 text-center">
                                                    <Badge variant="outline">{dept.avg_scores.communication}</Badge>
                                                </td>
                                                <td className="py-2 text-center">
                                                    <Badge variant="outline">{dept.avg_scores.simulation}</Badge>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </TableBody>
                            </table>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Detail Modal for Stats */}
            <Dialog open={detailModal.open} onOpenChange={(open) => !open && setDetailModal({ ...detailModal, open: false })}>
                <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            {detailModal.type === 'total_users' && <Users className="h-5 w-5 text-primary" />}
                            {detailModal.type === 'active_users' && <Activity className="h-5 w-5 text-emerald-500" />}
                            {detailModal.type === 'total_sessions' && <Target className="h-5 w-5 text-blue-500" />}
                            {detailModal.type === 'avg_completion' && <TrendingUp className="h-5 w-5 text-purple-500" />}
                            {detailModal.title}
                        </DialogTitle>
                        <DialogDescription>
                            {detailModal.type?.includes('users') ? 'View and manage users' : 'Training session details'}
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="flex-1 overflow-auto">
                        {detailModal.loading ? (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                            </div>
                        ) : (
                            <>
                                {/* Users Table */}
                                {(detailModal.type === 'total_users' || detailModal.type === 'active_users') && (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Name</TableHead>
                                                <TableHead>Email</TableHead>
                                                <TableHead>Department</TableHead>
                                                <TableHead>Role</TableHead>
                                                <TableHead>Last Login</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {detailModal.data?.length > 0 ? detailModal.data.map((user) => (
                                                <TableRow key={user.id || user.email}>
                                                    <TableCell className="font-medium">{user.name}</TableCell>
                                                    <TableCell>{user.email}</TableCell>
                                                    <TableCell>{user.department || '-'}</TableCell>
                                                    <TableCell>
                                                        <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                                                            {user.role}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell className="text-muted-foreground">
                                                        {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                                                    </TableCell>
                                                </TableRow>
                                            )) : (
                                                <TableRow>
                                                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                                                        No users found
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                )}
                                
                                {/* Sessions Table */}
                                {detailModal.type === 'total_sessions' && (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>User</TableHead>
                                                <TableHead>Type</TableHead>
                                                <TableHead>Score</TableHead>
                                                <TableHead>Date</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {detailModal.data?.length > 0 ? detailModal.data.map((session, idx) => (
                                                <TableRow key={session.id || `session-${idx}`}>
                                                    <TableCell className="font-medium">{session.user_name || session.user_email || '-'}</TableCell>
                                                    <TableCell>{session.type || session.scenario_type || '-'}</TableCell>
                                                    <TableCell>
                                                        <Badge variant="secondary">{session.score || session.final_score || 0}</Badge>
                                                    </TableCell>
                                                    <TableCell className="text-muted-foreground">
                                                        {session.completed_at ? new Date(session.completed_at).toLocaleDateString() : '-'}
                                                    </TableCell>
                                                </TableRow>
                                            )) : (
                                                <TableRow>
                                                    <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                                                        No sessions found
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                )}
                                
                                {/* Completion Stats */}
                                {detailModal.type === 'avg_completion' && (
                                    <div className="grid gap-4 p-4">
                                        {Object.entries(features).map(([key, data]) => (
                                            <div key={key} className="flex items-center justify-between p-4 rounded-lg border">
                                                <div>
                                                    <p className="font-medium capitalize">{key.replace(/_/g, ' ')}</p>
                                                    <p className="text-sm text-muted-foreground">
                                                        {data?.completed || 0} of {data?.total_sessions || 0} completed
                                                    </p>
                                                </div>
                                                <div className="flex items-center gap-4">
                                                    <Progress value={data?.completion_rate || 0} className="w-32" />
                                                    <Badge variant="secondary">{data?.completion_rate || 0}%</Badge>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    
                    {/* Pagination */}
                    {(detailModal.type === 'total_users' || detailModal.type === 'active_users' || detailModal.type === 'total_sessions') && 
                     pagination.total > pagination.limit && (
                        <div className="flex items-center justify-between pt-4 border-t">
                            <p className="text-sm text-muted-foreground">
                                Page {pagination.page} of {Math.ceil(pagination.total / pagination.limit)}
                            </p>
                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => fetchDetailPage(pagination.page - 1)}
                                    disabled={pagination.page === 1 || detailModal.loading}
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                    Previous
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => fetchDetailPage(pagination.page + 1)}
                                    disabled={pagination.page >= Math.ceil(pagination.total / pagination.limit) || detailModal.loading}
                                >
                                    Next
                                    <ChevronRight className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminComprehensiveDashboard;
